import { Component, EventEmitter, Input, OnChanges, OnInit, Output, OnDestroy, SimpleChanges } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ProjectTable } from 'src/app/components/project-table/project-table.component';
import { Subscription } from 'rxjs';
import { formatCPF, formatCNPJ, formatCPFOrCNPJ, extractDocumentCharacters } from 'src/app/helpers/utils/cpf-cnpj.util';
import { Beneficiaries } from 'src/app/shared/interfaces/beneficiaries';
import { AppToastService } from 'src/app/shared/services/app-toast.service';
import { WithdrawalBeneficiaryToastState } from './withdrawal-beneficiary-toast.model';

@Component({
  standalone: false,
  selector: 'app-psa-add-beneficiary',
  templateUrl: './psa-add-beneficiary.component.html',
  styleUrls: ['./psa-add-beneficiary.component.scss'],
})
export class AddOthersBeneficiariesComponent extends ProjectTable<Beneficiaries> implements OnInit, OnDestroy, OnChanges {
  @Input() formRegister!: FormGroup;
  @Output() developersChanged = new EventEmitter<Beneficiaries[]>();

  @Input() disabled: boolean = true;
  @Input() creditQuantity: number = 0;
  foreigner: boolean = false;
  private previousForeigner = false;
  namedocumentNumber: string = 'CPF/CNPJ';
  @Output() isApprovedQuantity = new EventEmitter<boolean>(false);
  @Output() accumulatedAmount = new EventEmitter<number>();
  @Output() foreignEntityModalRequest = new EventEmitter<{
    isForeigner: boolean;
    previousForeigner: boolean;
  }>();
  @Output() attentionModalRequest = new EventEmitter<void>();

  developersAdded: Beneficiaries[] = [];
  developersSubscription!: Subscription | undefined;
  @Input() clearFormNow: boolean = false;
  onlyNumbersInvalid: boolean = false;
  valorBar: number = 0;
  accumulatedWithdrawalAmount: number = 0;
  isDetailsDrawerOpen = false;
  selectedBeneficiary: Beneficiaries | null = null;
  activeToast: WithdrawalBeneficiaryToastState | null = null;

  constructor(
    private b3ToastService: AppToastService
  ) {
    super();
  }

  ngOnInit(): void {
    const initialDevelopers = this.formRegister.get('beneficiaries')?.value;
    if (initialDevelopers && Array.isArray(initialDevelopers)) {
      this.developersAdded = initialDevelopers.filter(d => !d.principal);
      this.updateTableDataOthersDevelopers();
    }

    this.developersSubscription = this.formRegister.get('beneficiaries')?.valueChanges.subscribe((value: Beneficiaries[]) => {
      if (value && Array.isArray(value)) {
        this.updateTableDataOthersDevelopers();
      }
    });

    this.formRegister.get('developerCnpjAdd')?.valueChanges.subscribe((value) => {
      if (this.foreigner) {
        return;
      }

      this.syncDocumentControlValue(value, this.shouldFormatDocumentOnInput(value));
    });
  }

  onChangesInputCpfOrCnpj(): void {
    if (this.foreigner) {
      return;
    }

    const control = this.formRegister.get('developerCnpjAdd');
    this.syncDocumentControlValue(control?.value, true);
  }

  private shouldFormatDocumentOnInput(value: string | null | undefined): boolean {
    return extractDocumentCharacters(value).length === 14;
  }

  private normalizeDocumentValue(raw: string | null | undefined, formatWhenComplete = false): string {
    const rawAlnum = extractDocumentCharacters(raw);
    const alnum = rawAlnum.slice(0, 14);
    if (!alnum) {
      return '';
    }

    const wasTruncated = rawAlnum.length > 14;

    if (alnum.length === 14 && (formatWhenComplete || wasTruncated)) {
      return formatCNPJ(alnum);
    }

    if (formatWhenComplete && alnum.length === 11 && !/[A-Za-z]/.test(alnum)) {
      return formatCPF(alnum);
    }

    return alnum;
  }

  private syncDocumentControlValue(raw: string | null | undefined, formatWhenComplete = false): void {
    const control = this.formRegister.get('developerCnpjAdd');
    const nextValue = this.normalizeDocumentValue(raw, formatWhenComplete);
    const currentAlnum = extractDocumentCharacters(control?.value);
    const nextAlnum = extractDocumentCharacters(nextValue);

    if (currentAlnum === nextAlnum && String(control?.value ?? '') === nextValue) {
      return;
    }

    control?.setValue(nextValue, { emitEvent: false });
  }

  ngOnDestroy(): void {
    if (this.developersSubscription) {
      this.developersSubscription.unsubscribe();
    }
    this.foreigner = false;
  }

  addDevelopers() {
    const beneficiaryName = this.formRegister.get('developerCompanyNameAdd')?.value?.trim();
    const documentNumber = this.formRegister.get('developerCnpjAdd')?.value?.trim();
    const beneficiaryDescription = this.formRegister.get('developerDescriptionAdd')?.value?.trim();
    const foreignerIndicator = this.formRegister.value.developerForeigner;
    const withdrawalAmount = this.formRegister.get('developerAmount')?.value?.trim();

    if (beneficiaryName && documentNumber) {
      const developer: Beneficiaries = {
        beneficiaryName,
        documentNumber,
        beneficiaryDescription,
        foreignerIndicator,
        withdrawalAmount
      };

      const existsDeveloper = this.developersAdded.find(d => d.documentNumber === developer.documentNumber
        && d.beneficiaryName === developer.beneficiaryName && d.beneficiaryDescription == developer.beneficiaryDescription);

      if (!existsDeveloper) {
        this.developersAdded.push(developer);
        this.updateTableDataOthersDevelopers();

        this.formRegister.get('beneficiaries')?.setValue(this.developersAdded);

        this.developersChanged.emit(this.developersAdded);

        this.showBeneficiaryToast('success', '1 entidade incluída!', () => {
          this.undoAddDeveloper(developer);
        });
      }

      this.formRegister.get('developerCompanyNameAdd')?.setValue('');
      this.formRegister.get('developerCnpjAdd')?.setValue('');
      this.formRegister.get('developerDescriptionAdd')?.setValue('');
      this.formRegister.get('developerForeigner')?.setValue(false);
      this.formRegister.get('developerAmount')?.setValue('');
    }
    this.verifiyAmount(true);
    this.foreigner = false;
    this.namedocumentNumber = this.documentFieldLabel;
  }

  deleteRetirement(retirement: Beneficiaries) {
    const index = this.developersAdded
      .findIndex(d => d.documentNumber === retirement.documentNumber && d.beneficiaryName === retirement.beneficiaryName);
    if (index !== -1) {
      const removedBeneficiary = this.developersAdded[index];
      this.developersAdded.splice(index, 1);
      this.updateTableDataOthersDevelopers();

      this.formRegister.get('beneficiaries')?.setValue(this.developersAdded);
      this.developersChanged.emit(this.developersAdded);

      this.showBeneficiaryToast('warning', '1 entidade excluída!', () => {
        this.undoDeleteDeveloper(removedBeneficiary, index);
      });
    }
    this.verifiyAmount(true);
  }

  onPageChange(currentPage: number): void {
    this.pagination.page = currentPage;
  }

  private updateTableDataOthersDevelopers() {
    this.itens = [...this.developersAdded];
    this.totalItens = this.developersAdded.length || 0;
    this.handleTotalPages({ pageSize: 10 });
  }

  checkHasError(fieldName: string, errorCode?: string): boolean {
    let field = this.formRegister.get(fieldName);

    if (field == null || !field.touched) {
      return false;
    }
    if (errorCode == null) {

      return field.invalid;
    }
    return field.hasError(errorCode);
  }

  checkHasErrorDigitCounter(fieldName: string): boolean {
    const field = this.formRegister.get(fieldName);
    const counter = extractDocumentCharacters(field?.value ?? '').length;
    if (counter === 0 || counter === 11 || counter === 14) {
      return false;
    }
    return true;
  }

  validateDisabledButton(): boolean {
    if (this.disabled) {
      return true;
    }

    const beneficiaryName = this.formRegister.get('developerCompanyNameAdd')?.value?.trim();
    const documentNumber = this.formRegister.get('developerCnpjAdd')?.value?.trim();

    if (!beneficiaryName || !documentNumber) {
      return true;
    }

    if (this.foreigner) {
      return documentNumber.length > 30;
    }

    return this.checkHasErrorDigitCounter('developerCnpjAdd');
  }

  ngOnChanges(changes: SimpleChanges) {
    this.foreigner = this.formRegister.get('developerForeigner')?.value || false;
    this.namedocumentNumber = this.documentFieldLabel;
    try {
      const value = changes['clearFormNow'].currentValue;
      if (value) {
        this.cleanForm();
        this.clearFormNow = false;
      }
    } catch (error) {

    }
    try {
      const value = changes['disabled'].currentValue;
      this.disabled = value;
      if (value) {
        this.formRegister.get('developerCompanyNameAdd')?.setValue('');
        this.formRegister.get('developerCnpjAdd')?.setValue('');
        this.formRegister.get('developerDescriptionAdd')?.setValue('');
        this.formRegister.get('developerForeigner')?.setValue(false);
        this.formRegister.get('developerAmount')?.setValue('');
        this.formRegister.get('beneficiaries')?.setValue([]);
        this.itens = [];
      }
    } catch (error) {

    }
    this.verifiyAmount();
  }

  openModal(retirement: Beneficiaries | null, type: string): void {
    if (type === 'details') {
      this.selectedBeneficiary = retirement;
      this.isDetailsDrawerOpen = true;
    }
  }

  cleanForm() {
    this.formRegister.get('developerCompanyNameAdd')?.setValue('');
    this.formRegister.get('developerCnpjAdd')?.setValue('');
    this.formRegister.get('developerDescriptionAdd')?.setValue('');
    this.formRegister.get('developerAmount')?.setValue('');
    this.formRegister.get('developerForeigner')?.setValue(false);
    this.itens = [];
    this.developersAdded = [];
    this.formRegister.get('beneficiaries')?.setValue([]);
    this.foreigner = false;
    this.namedocumentNumber = this.documentFieldLabel;
    this.verifiyAmount();
  }

  onChangeForeigner(event: Event | { checked?: boolean }): void {
    const checked = event instanceof Event
      ? !!(event as CustomEvent<{ selected?: boolean }>).detail?.selected
      : !!event.checked;

    this.previousForeigner = !checked;
    this.foreigner = checked;
    this.formRegister.get('developerForeigner')?.setValue(checked);
    this.namedocumentNumber = this.documentFieldLabel;
    this.foreignEntityModalRequest.emit({
      isForeigner: checked,
      previousForeigner: this.previousForeigner,
    });
  }

  applyForeignEntityResult(value: boolean | null, previousForeigner: boolean): void {
    if (value === null) {
      this.foreigner = previousForeigner;
      this.formRegister.get('developerForeigner')?.setValue(previousForeigner);
      return;
    }

    this.foreigner = value;
    this.formRegister.get('developerForeigner')?.setValue(value);
    this.formRegister.get('developerCnpjAdd')?.setValue('');
    this.namedocumentNumber = this.documentFieldLabel;

    if (value !== previousForeigner) {
      this.showBeneficiaryToast(
        'info',
        value ? 'Alterado para Entidade Estrangeira' : 'Alterado para CPF/CNPJ',
        () => this.applyForeignEntityState(previousForeigner),
      );
    }
  }

  onToastUndo(): void {
    this.activeToast?.undo?.();
    this.dismissBeneficiaryToast();
  }

  onToastDismiss(): void {
    this.dismissBeneficiaryToast();
  }

  private showBeneficiaryToast(
    variant: WithdrawalBeneficiaryToastState['variant'],
    label: string,
    undo?: () => void,
  ): void {
    this.activeToast = {
      variant,
      label,
      open: true,
      undo,
    };
  }

  private dismissBeneficiaryToast(): void {
    this.activeToast = null;
  }

  private undoAddDeveloper(developer: Beneficiaries): void {
    const index = this.developersAdded.findIndex(
      item => item.documentNumber === developer.documentNumber
        && item.beneficiaryName === developer.beneficiaryName
        && item.beneficiaryDescription === developer.beneficiaryDescription,
    );

    if (index === -1) {
      return;
    }

    this.developersAdded.splice(index, 1);
    this.syncDevelopersState();
  }

  private undoDeleteDeveloper(developer: Beneficiaries, index: number): void {
    const safeIndex = Math.min(Math.max(index, 0), this.developersAdded.length);
    this.developersAdded.splice(safeIndex, 0, developer);
    this.syncDevelopersState();
  }

  private applyForeignEntityState(isForeigner: boolean): void {
    this.foreigner = isForeigner;
    this.formRegister.get('developerForeigner')?.setValue(isForeigner);
    this.formRegister.get('developerCnpjAdd')?.setValue('');
    this.namedocumentNumber = this.documentFieldLabel;
  }

  private syncDevelopersState(): void {
    this.updateTableDataOthersDevelopers();
    this.formRegister.get('beneficiaries')?.setValue(this.developersAdded);
    this.developersChanged.emit(this.developersAdded);
    this.verifiyAmount(true);
  }

  verifiyAmount(isAdd: boolean = false) {
    try {
      const form = this.formRegister.value as Record<string, any>;
      let beneficiaries: any[] = [];
      let withdrawalAmount: number = 0;
      Object.entries(form).forEach(([key, value]) => {
        if (key == "beneficiaries") {
          beneficiaries = value;
        }
      });
      beneficiaries.forEach((e: Beneficiaries) => {
        if (e.withdrawalAmount !== null && e.withdrawalAmount !== undefined) {
          withdrawalAmount += Number(e.withdrawalAmount);
        }
      })
      if ((Number(this.creditQuantity) > Number(withdrawalAmount)) && this.itens.length > 0) {
        this.isApprovedQuantity.emit(false);
        this.b3ToastService.show({
          status: 'info',
          text: `Quantidade restante ${Number(this.creditQuantity) - Number(withdrawalAmount)}`,
        });
      } else {
        this.isApprovedQuantity.emit(true);
      }

      if ((Number(this.creditQuantity) < Number(withdrawalAmount)) && isAdd) {
        this.accumulatedAmount.emit(withdrawalAmount);
        this.attentionModalRequest.emit();
      }

      this.accumulatedWithdrawalAmount = withdrawalAmount;
      this.valorBar = this.toPercentage(withdrawalAmount, this.creditQuantity);
    } catch (error) {

    }
  }

  get nameAddItem(): string {
    return '+ Incluir nova';
  }

  get documentFieldLabel(): string {
    return this.foreigner ? 'TAX ID' : 'CPF/CNPJ';
  }

  get developerDocumentMask(): 'text' | 'cpf' | 'cnpj' {
    if (this.foreigner) {
      return 'text';
    }

    const alnum = extractDocumentCharacters(this.formRegister.get('developerCnpjAdd')?.value ?? '');
    if (/[A-Za-z]/.test(alnum)) {
      return 'text';
    }

    return alnum.length >= 12 ? 'cnpj' : 'text';
  }

  get remainingQuantityValues(): string {
    const current = Number(this.accumulatedWithdrawalAmount);
    const total = Number(this.creditQuantity);
    return `${current}/${total}`;
  }

  get entitiesCounterLabel(): string {
    return String(this.itens.length);
  }

  get progressPercentageLabel(): string {
    const percentage = Number.isFinite(this.valorBar) ? this.valorBar : 0;
    const normalized = Number.isInteger(percentage) ? percentage : Number(percentage.toFixed(2));
    return `${normalized}%`;
  }

  formatDocumentNumber(value?: string): string {
    return formatCPFOrCNPJ(value ?? '');
  }

  private toPercentage(
    withdrawalAmount: number,
    total: number,
    decimals = 2
  ): number {
    if (!isFinite(withdrawalAmount) || !isFinite(total)) return 0;
    if (total < withdrawalAmount) return 100;
    if (total <= 0) return 0;
    const raw = (withdrawalAmount / total) * 100;
    const clamped = Math.min(100, Math.max(0, raw));
    const factor = Math.pow(10, decimals);
    return Math.round(clamped * factor) / factor;
  }

}